import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { AccentPicker } from "@/components/AccentPicker";
import { Logos } from "@/components/Logos";
import { MissionTicker } from "@/components/MissionTicker";
import { NoticeBand } from "@/components/NoticeBand";
import { SignInHero } from "@/components/SignInHero";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const INPUT =
  "mt-2 h-12 rounded-none border-zinc-800 bg-zinc-900/60 px-4 font-data text-zinc-100 placeholder:text-zinc-600 focus-visible:border-[color:var(--acc-focus)] focus-visible:ring-1 focus-visible:ring-[color:var(--acc-focus)] focus-visible:ring-offset-0";
const LABEL = "font-data text-[10px] uppercase tracking-[0.2em] text-zinc-500";
const SUBMIT =
  "flex h-12 w-full items-center justify-center gap-2 rounded-none bg-[color:var(--acc-mid)] font-display text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors duration-200 hover:bg-[color:var(--acc-deep)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--acc-mid)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 disabled:cursor-not-allowed disabled:opacity-40";

export default function Login() {
  const [mode, setMode] = useState("passcode");
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const signInWithPassword = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login-password", { email, password });
      login(data.token, data.user);
      if (data.must_change_password) {
        toast.info("Please set your own password before you go in.");
        navigate("/set-password", { replace: true });
        return;
      }
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const requestOtp = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email });
      setInfo(data);
      setStep("otp");
      toast.success(data.message);
      // A standing passcode is not mailed at all, and the portal does not say so. Only a real
      // relay failure — one that names itself — is worth a red toast.
      if (!data.fixed_passcode && !data.email_sent && data.email_error)
        toast.error(`Could not send the code: ${data.email_error}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code });
      login(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-zinc-950 pb-9">
      <div className="grid min-h-screen bg-zinc-950 lg:grid-cols-[1.2fr_1fr]">
      <SignInHero />

      <div className="hero-grid relative flex items-center justify-center px-6 py-16 lg:px-12">
        <div className="w-full max-w-[400px]">
          <div className="mb-10 inline-flex items-center gap-3 border border-white/10 bg-white/[0.04] p-2 backdrop-blur-md lg:hidden">
            <Logos boxed size="h-10" />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="font-data text-[10px] uppercase tracking-[0.24em] text-[color:var(--acc-bright)]">
              Secure sign-in · SAIL officers
            </div>
            <AccentPicker variant="dark" />
          </div>
          <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-zinc-50">
            {mode === "password"
              ? "Sign in"
              : step === "email"
                ? "Sign in"
                : "Enter your passcode"}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            {mode === "password"
              ? "Use your SAIL email and your portal password. If you have never set one, it is the part of your email before the @."
              : step === "email"
                ? "Use your SAIL email. We will send you a 6-digit passcode — no password needed."
                : `We sent a 6-digit passcode to ${email}.`}
          </p>

          <div className="mt-7 grid grid-cols-2 border border-zinc-800" data-testid="login-mode">
            {[
              ["passcode", "6-digit passcode"],
              ["password", "Password"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={`login-mode-${key}`}
                onClick={() => {
                  setMode(key);
                  setStep("email");
                  setCode("");
                  setPassword("");
                }}
                className={`py-3 font-data text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                  mode === key
                    ? "bg-[color:var(--acc-mid)] text-white"
                    : "text-zinc-500 hover:bg-zinc-900/60 hover:text-zinc-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === "password" ? (
            <form onSubmit={signInWithPassword} className="mt-7 space-y-5">
              <div>
                <label className={LABEL} htmlFor="pw-email">
                  Your SAIL email
                </label>
                <Input
                  id="pw-email"
                  type="email"
                  required
                  autoFocus
                  data-testid="password-email-input"
                  placeholder="name@sail.in"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={INPUT}
                />
              </div>
              <div>
                <label className={LABEL} htmlFor="pw">
                  Your password
                </label>
                <Input
                  id="pw"
                  type="password"
                  required
                  data-testid="password-input"
                  placeholder="Your portal password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={INPUT}
                />
              </div>
              <button
                type="submit"
                disabled={busy || !email || !password}
                data-testid="password-login-button"
                className={SUBMIT}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Sign in <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
              <div className="flex items-start gap-2 border border-zinc-800 bg-zinc-900/60 p-3 text-xs text-zinc-400">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--acc-bright)]" />
                <span>
                  Forgotten it? Ask an SDTD administrator to reset it, or use the 6-digit passcode
                  instead — that way in is always open.
                </span>
              </div>
            </form>
          ) : step === "email" ? (
            <form onSubmit={requestOtp} className="mt-7 space-y-5">
              <div>
                <label className={LABEL} htmlFor="email">
                  Your SAIL email
                </label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  data-testid="login-email-input"
                  placeholder="name@sail.in"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={INPUT}
                />
              </div>
              <button
                type="submit"
                disabled={busy || !email}
                data-testid="request-otp-button"
                className={SUBMIT}
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Send passcode <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-9 space-y-5">
              <div>
                <label className={LABEL} htmlFor="otp">
                  6-digit passcode
                </label>
                <Input
                  id="otp"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  autoFocus
                  data-testid="otp-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-2 h-16 rounded-none border-zinc-800 bg-zinc-900/60 text-center font-data text-3xl tracking-[0.5em] text-zinc-100 focus-visible:border-[color:var(--acc-focus)] focus-visible:ring-1 focus-visible:ring-[color:var(--acc-focus)] focus-visible:ring-offset-0"
                />
              </div>
              {info?.fixed_passcode && (
                <div
                  className="flex items-start gap-2 border border-zinc-800 bg-zinc-900/60 p-3 text-xs text-zinc-300"
                  data-testid="fixed-passcode-note"
                >
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--acc-bright)]" />
                  <span>{info?.message || "You are an SDTD administrator — use your standing passcode."}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                data-testid="verify-otp-button"
                className={SUBMIT}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & enter"}
              </button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  data-testid="back-to-email-button"
                  className="flex items-center gap-1 text-zinc-500 transition-colors duration-200 hover:text-zinc-200"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                  }}
                >
                  <ArrowLeft className="h-3 w-3" /> Change email
                </button>
                <button
                  type="button"
                  data-testid="resend-otp-button"
                  className="text-[color:var(--acc-bright)] transition-opacity duration-200 hover:opacity-75"
                  onClick={requestOtp}
                >
                  Send it again
                </button>
              </div>
            </form>
          )}

          <div className="mt-12 border-t border-zinc-800 pt-5 font-data text-[10px] uppercase tracking-[0.16em] text-zinc-600">
            Access restricted to @sail.in identities
          </div>
        </div>
      </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-20">
        <NoticeBand where="login" />
        <MissionTicker variant="dark" />
      </div>
    </div>
  );
}
